import './SearchableSelect.less';
import React from 'react';
import {translate} from "../../i18n";
import {slugify} from '../../utils/string';
import Dropdown from "../dropdown/Dropdown";
import Icon from "../icon/Icon";
import {compareArrays} from "../../utils/js";

const TEXT_INPUT_PADDING = 2;

export default class SearchableSelect extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            value: SearchableSelect.normalizeValue(this.props.defaultValue, this.props.isSingle),
            optionsDropdownShown: false,
            searchText: '',
            searchTextInputWidth: TEXT_INPUT_PADDING,
            focusedOptionIndex: 0
        };

        /** @type {HTMLInputElement} */
        this.searchTextInput = null;

        /** @type {HTMLLIElement} */
        this.searchTextListItem = null;

        /** @type {HTMLDivElement} */
        this.inlineEl = null;

        /** @type {HTMLDivElement} */
        this.dropdownEl = null;

        /** @type {HTMLDivElement} */
        this.dropdownScrollView = null;

        /** @type {HTMLLIElement} */
        this.focusedOptionEl = null;

        /** @type {HTMLUListElement} */
        this.selectionListEl = null;

        /** @type {CanvasRenderingContext2D} */
        this.context2d = document.createElement('canvas').getContext('2d');
    }

    static getDerivedStateFromProps(props, state) {
        if (props.value !== undefined) {
            state.value = SearchableSelect.normalizeValue(props.value, props.isSingle);

            return state;
        }

        if (props.imperativeValue !== undefined && (state.prevImperativeValue === undefined ||
            !compareArrays(props.imperativeValue, state.prevImperativeValue)))
        {
            state.prevImperativeValue = props.imperativeValue;

            state.value = SearchableSelect.normalizeValue(props.imperativeValue, props.isSingle);

            return state;
        }

        return null;
    }

    static normalizeValue = (value, isSingle) => {
        if (isSingle) {
            return value !== undefined && value !== null ? [value] : [];
        }

        return value !== undefined ? [].concat(value) : [];
    };

    setValue = (value) => {
        const {onChange, isSingle} = this.props;

        this.setState(
            {value},
            onChange ? () => {
                const {value} = this.state;

                if (isSingle) {
                    if (value.length > 0) {
                        onChange(value[0]);
                        return;
                    }

                    onChange(null);
                    return;
                }

                onChange(value);
            } : undefined
        );
    };

    deselectAll = () => {
        const {value} = this.state;
        if (value.length === 0) {
            return;
        }
        this.setValue([]);
    };

    deselect = (optionValue) => {
        const {value} = this.state;
        if (!value.includes(optionValue)) {
            return;
        }
        this.setValue(value.filter(v => v !== optionValue));
    };

    select = (optionValue) => {
        const {isSingle} = this.props;
        const {value} = this.state;
        if (value.includes(optionValue)) {
            return;
        }
        this.setValue(isSingle ? [optionValue] : value.concat([optionValue]));
    };

    handleSelect = (optionValue) => {
        this.select(optionValue);

        if (this.props.isSingle) {
            this.blur();
        }
    };

    handleDeselect = (optionValue) => {
        this.deselect(optionValue);

        if (this.props.isSingle) {
            this.blur();
        }
    };

    blur = () => {
        this.closeOptionsDropdown();
        this.clearSearchText();
        this.blurSearchTextInput();
    };

    setSearchText = (searchText) => {
        if (searchText === this.state.searchText) {
            return;
        }
        const style = window.getComputedStyle(this.searchTextInput);
        this.context2d.font = style.font;
        const itemStyle = window.getComputedStyle(this.searchTextListItem);
        const itemMarginX = parseFloat(itemStyle.marginLeft) + parseFloat(itemStyle.marginRight);
        const searchTextInputWidth = Math.min(
            this.selectionListEl.clientWidth - itemMarginX,
            this.context2d.measureText(searchText).width + TEXT_INPUT_PADDING
        );
        this.setState({
            searchText,
            searchTextInputWidth,
            focusedOptionIndex: 0
        });
    };

    clearSearchText = () => {
        this.setSearchText('');
    };

    blurSearchTextInput = () => {
        this.searchTextInput.blur();
    };

    focusSearchTextInput = () => {
        this.searchTextInput.focus();
    };

    handleEnterEvent = (focusedOptionIndex, shownOptions) => {
        const option = shownOptions[focusedOptionIndex];
        if (option === undefined) {
            return;
        }
        this.handleSelect(option.value);
    };

    handleKeyDownEvent = (ev, shownOptions) => {
        const {isSingle, clearable, selectionsHidden} = this.props;
        const {focusedOptionIndex, searchText, value} = this.state;

        if (ev.key === 'Enter' || ev.key === 'Tab') {
            ev.preventDefault();
            this.handleEnterEvent(focusedOptionIndex, shownOptions);
            return;
        }

        if (ev.key === 'ArrowUp') {
            ev.preventDefault();
            this.setState({
                focusedOptionIndex: Math.max(0, focusedOptionIndex - 1)
            });
            return;
        }

        if (ev.key === 'ArrowDown') {
            ev.preventDefault();
            this.setState({
                focusedOptionIndex: Math.min(shownOptions.length - 1, focusedOptionIndex + 1)
            });
            return;
        }

        if (ev.key === 'Home') {
            ev.preventDefault();
            this.setState({
                focusedOptionIndex: 0
            });
            return;
        }

        if (ev.key === 'End') {
            ev.preventDefault();
            this.setState({
                focusedOptionIndex: shownOptions.length - 1
            });
            return;
        }

        if (!selectionsHidden && searchText === '' && (ev.key === 'Backspace' || ev.key === 'Delete')) {
            ev.preventDefault();

            if (value.length > 0) {
                if (isSingle) {
                    if (clearable) {
                        this.deselectAll();
                    }
                    return;
                }

                this.deselect(value[value.length - 1]);
            }
        }
    };


    toggleOptionsDropdown = () => {
        this.setState({
            optionsDropdownShown: !this.state.optionsDropdownShown
        });
    };

    closeOptionsDropdown = () => {
        if (this.state.optionsDropdownShown) {
            this.setState({
                optionsDropdownShown: false
            });
        }
    };

    openOptionsDropdown = () => {
        if (!this.state.optionsDropdownShown) {
            this.setState({
                optionsDropdownShown: true
            });
        }
    };

    setFocusedOptionIndex = (index) => {
        if (index !== this.state.focusedOptionIndex) {
            this.setState({
                focusedOptionIndex: index
            });
        }
    };

    updateDropdownScroll = () => {
        if (this.focusedOptionEl === null || this.dropdownScrollView === null) {
            return;
        }

        const dropdownHeight = this.dropdownScrollView.clientHeight;
        const dropdownScrollTop = this.dropdownScrollView.scrollTop;
        const optionHeight = this.focusedOptionEl.offsetHeight;
        const optionTop = this.focusedOptionEl.offsetTop - this.dropdownScrollView.offsetTop;

        if (dropdownScrollTop < optionTop - (dropdownHeight - optionHeight)) {
            this.dropdownScrollView.scrollTop = optionTop - (dropdownHeight - optionHeight);
            return;
        }

        if (dropdownScrollTop > optionTop) {
            this.dropdownScrollView.scrollTop = optionTop;
        }
    };

    /**
     *
     * @param {LabelMap} options
     * @return {*}
     */
    getSearchingMatchedOptions = (options) => {
        const {searchText} = this.state;

        if (searchText.trim() === '') {
            return options.items;
        }

        const rankingTable = [];

        const EXACT_PER_SLUG = 100;
        const POS_PER_NUM = 10;
        const SHORT_PER_LENGTH = 5;

        options.items.forEach(option => {
            const label = option.label;
            const searchableName = option.extra ? option.extra.searchableName : undefined;
            const matcher = searchableName || String(label);
            const matcherSlug = slugify(matcher);
            const searchTextSlug = slugify(searchText);
            const exactPos = matcher.indexOf(searchText);
            const slugPos = matcherSlug.indexOf(searchTextSlug);
            if (exactPos < 0 && slugPos < 0) {
                return;
            }
            let score = 0;
            if (exactPos >= 0) {
                const exactNum = matcher.split(searchText).length - 1;
                score += (exactNum - exactPos * POS_PER_NUM) * EXACT_PER_SLUG;
            }
            if (slugPos >= 0) {
                const slugNum = matcherSlug.split(searchTextSlug).length - 1;
                score += (slugNum - slugPos * POS_PER_NUM);
            }
            score += (-matcher.length) * SHORT_PER_LENGTH;
            rankingTable.push([option, score]);
        });

        rankingTable.sort((a, b) => {
            if (b[1] > a[1]) return 1;
            if (b[1] < a[1]) return -1;
            return 0;
        });

        return rankingTable.map(item => item[0]);
    };

    render() {
        const {value: inputValue, optionsDropdownShown, searchText, searchTextInputWidth, focusedOptionIndex} = this.state;
        const {isSingle, disabled, clearable, selectionsHidden, prompt = '', dropdownHorizontalAlignment = 'left'} = this.props;
        /** @type {LabelMap} */
        const options = this.props.options;
        const shownOptions = this.getSearchingMatchedOptions(options);

        // reset
        this.focusedOptionEl = null;

        return <div
            className={
                'searchable-select'
                + (disabled ? ' disabled' : '')
            }
        >
            <div className="inline"
                 onClick={() => {
                     if (disabled) {
                         return;
                     }
                     this.focusSearchTextInput();
                     this.openOptionsDropdown();
                 }}
                 ref={el => this.inlineEl = el}
            >
                <div className="selections">
                    {
                        searchText === '' &&
                        (selectionsHidden || inputValue.length === 0) &&
                        <div className="prompt">
                            <div className="text">{prompt}</div>
                        </div>
                    }
                    <ul ref={el => this.selectionListEl = el}>
                        {
                            !selectionsHidden &&
                            inputValue
                                .map(value => {
                                    const option =  options.get(value);

                                    return <li key={String(option.value)}>
                                        {
                                            isSingle ?
                                                <div className="single-selection">
                                                    <div className="content">{option.label}</div>
                                                </div>
                                                :
                                                <div className="selection">
                                                    <div className="content">{option.label}</div>
                                                    {
                                                        disabled ||
                                                        <button
                                                            className="remove-button"
                                                            type="button"
                                                            onClick={ev => {
                                                                ev.stopPropagation();
                                                                this.deselect(option.value);
                                                            }}
                                                        >
                                                            <Icon name="times"/>
                                                        </button>
                                                    }
                                                </div>
                                        }
                                    </li>;
                                })
                        }
                        {
                            disabled ||
                            <li
                                ref={el => this.searchTextListItem = el}
                                className={!optionsDropdownShown ? 'zero-space' : ''}
                            >
                                <input
                                    className="search-text-input"
                                    type="text"
                                    value={searchText}
                                    onKeyUp={ev => this.setSearchText(ev.target.value)}
                                    onPaste={ev => this.setSearchText(ev.target.value)}
                                    onChange={ev => this.setSearchText(ev.target.value)}
                                    onKeyDown={ev => this.handleKeyDownEvent(ev, shownOptions)}
                                    ref={el => this.searchTextInput = el}
                                    style={{
                                        width: searchTextInputWidth + 'px'
                                    }}
                                />
                            </li>
                        }
                    </ul>
                </div>
                {
                    disabled ||
                    <div className="buttons">
                        {
                            clearable &&
                            inputValue.length > 0
                                ? <button
                                    type="button"
                                    className="clear-button"
                                    onClick={ev => {
                                        ev.stopPropagation();
                                        this.deselectAll();
                                    }}
                                >
                                    <Icon name="times"/>
                                </button>
                                : <button
                                    type="button"
                                    className="caret-button"
                                >
                                    <Icon name="caret-down"/>
                                </button>
                        }
                    </div>
                }
            </div>
            {
                !disabled &&
                optionsDropdownShown &&
                <Dropdown
                    name="searchable-select"
                    opener={this.inlineEl}
                    minWidth="anchorWidth"
                    horizontalAlignment={dropdownHorizontalAlignment}
                    close={() => {
                        this.closeOptionsDropdown();
                        this.clearSearchText();
                    }}
                    onRefreshDisplay={this.updateDropdownScroll}
                >
                    {
                        (contentViewableMaxHeight) =>
                            <div
                                className={(appearance ? ` appearance-${appearance}` : '')}
                                ref={el => this.dropdownEl = el}
                            >
                                <div className="dropdown-scroll-container">
                                    <div
                                        className="dropdown-scroll-view"
                                        ref={el => this.dropdownScrollView = el}
                                        style={{
                                            maxHeight: contentViewableMaxHeight + 'px'
                                        }}
                                    >
                                        {
                                            shownOptions.length > 0 &&
                                            <ul>
                                                {
                                                    shownOptions
                                                        .map((option, index) => {
                                                            const selected = inputValue.indexOf(option.value) >= 0;
                                                            const focused = focusedOptionIndex === index;
                                                            return <li
                                                                key={String(option.value)}
                                                                className={
                                                                    (selected ? ' selected' : '') +
                                                                    (focused ? ' focused' : '')
                                                                }
                                                                onMouseOver={() => this.setFocusedOptionIndex(index)}
                                                                onClick={() => {
                                                                    this.focusSearchTextInput();
                                                                    this.handleSelect(option.value);
                                                                }}
                                                                ref={el => {
                                                                    if (focused) {
                                                                        this.focusedOptionEl = el;
                                                                    }
                                                                }}
                                                            >
                                                                <div className="option">
                                                                    {option.label}
                                                                </div>
                                                                {
                                                                    !isSingle &&
                                                                    <button
                                                                        type="button"
                                                                        className="remove-button"
                                                                        onClick={(ev) => {
                                                                            ev.stopPropagation();
                                                                            this.focusSearchTextInput();
                                                                            this.handleDeselect(option.value);
                                                                        }}
                                                                    >
                                                                        {translate('Remove')}
                                                                    </button>
                                                                }
                                                            </li>;
                                                        })
                                                }
                                            </ul>
                                        }
                                        {
                                            shownOptions.length === 0 &&
                                            <div className="empty-placeholder">
                                                {translate('No options')}
                                            </div>
                                        }
                                    </div>
                                </div>
                            </div>
                    }
                </Dropdown>
            }
        </div>
    }
}
